/**
 * Configuration complète des ressources GCP
 * Définit les champs requis, optionnels et les suggestions pour chaque type de ressource
 */

export interface FieldConfig {
  key: string;
  label: string;
  type: 'text' | 'select' | 'number' | 'boolean' | 'textarea';
  required: boolean;
  default?: any;
  options?: string[];
  suggestions?: string[];
  placeholder?: string;
  description?: string;
  validation?: (value: any) => string | null;
}

export interface ResourceConfig {
  displayName: string;
  category: string;
  description: string;
  fields: FieldConfig[];
  priorityFields?: string[]; // Champs à afficher en priorité (mode simple)
  relationshipSuggestions?: {
    inbound?: string[];
    outbound?: string[];
  };
}

// Validation functions
const containsVariable = (value: string): boolean => /\{\{[^}]+\}\}/.test(value);

const validateName = (value: string): string | null => {
  if (!value) return "Le nom est requis";
  if (containsVariable(value)) return null;
  if (!/^[a-z]([a-z0-9-]{0,61}[a-z0-9])?$/.test(value)) {
    return "Nom invalide: commencer par une lettre minuscule, utiliser lettres, chiffres et tirets (1-63 caractères)";
  }
  return null;
};

const validateProjectId = (value: string): string | null => {
  if (!value) return "Le project ID est requis";
  if (containsVariable(value)) return null;
  if (!/^[a-z]([a-z0-9-]{4,28}[a-z0-9])$/.test(value)) {
    return "Project ID invalide: 6-30 caractères, lettres minuscules, chiffres et tirets";
  }
  return null;
};

const validateRegion = (value: string): string | null => {
  const validRegions = [
    'us-central1', 'us-east1', 'us-east4', 'us-west1', 'us-west2', 'us-west3', 'us-west4',
    'europe-west1', 'europe-west2', 'europe-west3', 'europe-west4', 'europe-west6', 'europe-north1', 'europe-central2',
    'asia-east1', 'asia-east2', 'asia-northeast1', 'asia-northeast2', 'asia-northeast3', 'asia-south1', 'asia-south2', 'asia-southeast1', 'asia-southeast2',
    'australia-southeast1', 'australia-southeast2',
    'southamerica-east1', 'southamerica-west1',
    'northamerica-northeast1', 'northamerica-northeast2'
  ];
  if (value && !validRegions.includes(value)) {
    return "Région GCP non valide";
  }
  return null;
};

const validateCIDR = (value: string): string | null => {
  if (!value) return null;
  if (!/^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/.test(value)) {
    return "Format CIDR invalide (ex: 10.0.0.0/16)";
  }
  return null;
};

export const GCP_RESOURCE_CONFIG: Record<string, ResourceConfig> = {
  'google_compute_network': {
    displayName: 'VPC Network',
    category: 'Network',
    description: 'Réseau VPC pour connecter vos ressources GCP',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'vpc-network',
        suggestions: ['vpc-prod', 'vpc-dev', 'main-network'],
        validation: validateName,
      },
      {
        key: 'project',
        label: 'Project ID',
        type: 'text',
        required: false,
        placeholder: 'my-project-id',
        description: 'ID du projet GCP',
      },
      {
        key: 'auto_create_subnetworks',
        label: 'Auto Create Subnetworks',
        type: 'boolean',
        required: false,
        default: false,
        description: 'Créer automatiquement des sous-réseaux',
      },
      {
        key: 'routing_mode',
        label: 'Routing Mode',
        type: 'select',
        required: false,
        default: 'REGIONAL',
        options: ['REGIONAL', 'GLOBAL'],
        description: 'Mode de routage du réseau',
      },
    ],
    relationshipSuggestions: {
      outbound: ['google_compute_subnetwork', 'google_compute_firewall'],
    },
  },

  'google_compute_subnetwork': {
    displayName: 'Subnetwork',
    category: 'Network',
    description: 'Sous-réseau à l\'intérieur d\'un VPC Network',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'subnet-web',
        suggestions: ['subnet-web', 'subnet-app', 'subnet-data'],
        validation: validateName,
      },
      {
        key: 'network',
        label: 'Network',
        type: 'text',
        required: true,
        description: 'Nom ou ID du VPC Network parent',
        placeholder: 'vpc-network',
      },
      {
        key: 'ip_cidr_range',
        label: 'IP CIDR Range',
        type: 'text',
        required: true,
        default: '10.0.1.0/24',
        suggestions: ['10.0.1.0/24', '10.0.2.0/24', '10.0.3.0/24'],
        description: 'Plage CIDR pour ce subnet',
        validation: validateCIDR,
      },
      {
        key: 'region',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'europe-west1',
        options: [
          'us-central1', 'us-east1', 'us-east4', 'us-west1', 'us-west2', 'us-west3', 'us-west4',
          'europe-west1', 'europe-west2', 'europe-west3', 'europe-west4', 'europe-west6', 'europe-north1', 'europe-central2',
          'asia-east1', 'asia-east2', 'asia-northeast1', 'asia-northeast2', 'asia-northeast3', 'asia-south1', 'asia-south2', 'asia-southeast1', 'asia-southeast2',
          'australia-southeast1', 'australia-southeast2',
          'southamerica-east1', 'southamerica-west1',
          'northamerica-northeast1', 'northamerica-northeast2'
        ],
        validation: validateRegion,
      },
      {
        key: 'private_ip_google_access',
        label: 'Private Google Access',
        type: 'boolean',
        required: false,
        default: false,
        description: 'Activer l\'accès privé aux APIs Google',
      },
    ],
    relationshipSuggestions: {
      inbound: ['google_compute_network'],
      outbound: ['google_compute_instance', 'google_container_cluster'],
    },
  },

  'google_storage_bucket': {
    displayName: 'Cloud Storage Bucket',
    category: 'Storage',
    description: 'Stockage d\'objets scalable et durable',
    fields: [
      {
        key: 'name',
        label: 'Nom du bucket',
        type: 'text',
        required: true,
        placeholder: 'my-app-bucket',
        suggestions: ['app-data-bucket', 'backup-bucket', 'static-assets'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Location',
        type: 'select',
        required: true,
        default: 'EU',
        options: [
          'US', 'EU', 'ASIA',
          'us-central1', 'us-east1', 'us-west1', 'us-west2', 'us-west3', 'us-west4', 'us-east4',
          'europe-west1', 'europe-west2', 'europe-west3', 'europe-west4', 'europe-west6', 'europe-north1', 'europe-central2',
          'asia-east1', 'asia-east2', 'asia-northeast1', 'asia-northeast2', 'asia-northeast3', 'asia-south1', 'asia-south2', 'asia-southeast1', 'asia-southeast2',
          'australia-southeast1', 'australia-southeast2',
          'southamerica-east1', 'southamerica-west1',
          'northamerica-northeast1', 'northamerica-northeast2'
        ],
        description: 'Localisation géographique du bucket (multi-région ou région spécifique)',
      },
      {
        key: 'storage_class',
        label: 'Storage Class',
        type: 'select',
        required: false,
        default: 'STANDARD',
        options: ['STANDARD', 'NEARLINE', 'COLDLINE', 'ARCHIVE'],
        description: 'Classe de stockage',
      },
      {
        key: 'uniform_bucket_level_access',
        label: 'Uniform Bucket Level Access',
        type: 'boolean',
        required: false,
        default: true,
        description: 'Utiliser IAM uniquement pour le contrôle d\'accès',
      },
      {
        key: 'versioning',
        label: 'Versioning',
        type: 'boolean',
        required: false,
        default: false,
        description: 'Activer le versioning des objets',
      },
      {
        key: 'capacity_gb',
        label: 'Total amount of storage (GB)',
        type: 'number',
        required: false,
        default: 1,
        placeholder: '1',
        description: 'Capacité de stockage estimée en GB pour le calcul des coûts',
      },
    ],
    relationshipSuggestions: {
      inbound: ['google_compute_instance', 'google_cloud_function', 'google_app_engine_application'],
    },
  },

  'google_compute_instance': {
    displayName: 'Compute Instance',
    category: 'Compute',
    description: 'Machine virtuelle pour exécuter des applications',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'instance-1',
        suggestions: ['web-server-1', 'app-server-1', 'worker-1'],
        validation: validateName,
      },
      {
        key: 'machine_type',
        label: 'Machine Type',
        type: 'select',
        required: true,
        default: 'e2-medium',
        options: [
          'e2-micro', 'e2-small', 'e2-medium', 'e2-standard-2', 'e2-standard-4',
          'n2-standard-2', 'n2-standard-4', 'n2-standard-8',
          'c2-standard-4', 'c2-standard-8', 'm1-ultramem-40'
        ],
        description: 'Type de machine',
      },
      {
        key: 'zone',
        label: 'Zone',
        type: 'text',
        required: true,
        placeholder: 'europe-west1-b',
        suggestions: ['europe-west1-b', 'us-central1-a', 'asia-east1-a'],
        description: 'Zone de disponibilité',
      },
      {
        key: 'boot_disk_image',
        label: 'Boot Disk Image',
        type: 'text',
        required: true,
        placeholder: 'debian-cloud/debian-11',
        suggestions: ['debian-cloud/debian-11', 'ubuntu-os-cloud/ubuntu-2204-lts', 'centos-cloud/centos-8'],
        description: 'Image du disque de démarrage',
      },
      {
        key: 'boot_disk_size',
        label: 'Boot Disk Size (GB)',
        type: 'number',
        required: false,
        default: 20,
        description: 'Taille du disque de démarrage en GB',
      },
      {
        key: 'network',
        label: 'Network',
        type: 'text',
        required: false,
        default: 'default',
        placeholder: 'vpc-network',
        description: 'Réseau VPC',
      },
      {
        key: 'subnetwork',
        label: 'Subnetwork',
        type: 'text',
        required: false,
        placeholder: 'subnet-web',
        description: 'Sous-réseau',
      },
    ],
    relationshipSuggestions: {
      inbound: ['google_compute_subnetwork', 'google_compute_firewall'],
      outbound: ['google_compute_disk', 'google_storage_bucket'],
    },
  },

  'google_app_engine_application': {
    displayName: 'App Engine Application',
    category: 'Web',
    description: 'Plateforme managée pour applications web',
    fields: [
      {
        key: 'project',
        label: 'Project ID',
        type: 'text',
        required: false,
        placeholder: 'my-project-id',
        description: 'ID du projet GCP',
      },
      {
        key: 'location_id',
        label: 'Location',
        type: 'select',
        required: true,
        default: 'europe-west',
        options: ['us-central', 'us-east1', 'europe-west', 'europe-west2', 'asia-northeast1'],
        description: 'Localisation de l\'application',
      },
      {
        key: 'serving_status',
        label: 'Serving Status',
        type: 'select',
        required: false,
        default: 'SERVING',
        options: ['SERVING', 'USER_DISABLED'],
        description: 'Statut de service de l\'application',
      },
    ],
    relationshipSuggestions: {
      outbound: ['google_sql_database_instance', 'google_storage_bucket', 'google_secret_manager_secret'],
    },
  },

  'google_sql_database_instance': {
    displayName: 'Cloud SQL Instance',
    category: 'Database',
    description: 'Base de données relationnelle managée',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'mysql-instance',
        suggestions: ['prod-db', 'app-database', 'mysql-db'],
        validation: validateName,
      },
      {
        key: 'database_version',
        label: 'Database Version',
        type: 'select',
        required: true,
        default: 'MYSQL_8_0',
        options: [
          'MYSQL_8_0', 'MYSQL_5_7',
          'POSTGRES_14', 'POSTGRES_13', 'POSTGRES_12',
          'SQLSERVER_2019_STANDARD', 'SQLSERVER_2019_WEB', 'SQLSERVER_2017_STANDARD', 'SQLSERVER_2017_WEB'
        ],
        description: 'Version du moteur de base de données (Web edition = moins cher pour SQL Server)',
      },
      {
        key: 'region',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'europe-west1',
        options: [
          'us-central1', 'us-east1', 'us-east4', 'us-west1', 'us-west2', 'us-west3', 'us-west4',
          'europe-west1', 'europe-west2', 'europe-west3', 'europe-west4', 'europe-west6', 'europe-north1', 'europe-central2',
          'asia-east1', 'asia-east2', 'asia-northeast1', 'asia-northeast2', 'asia-northeast3', 'asia-south1', 'asia-south2', 'asia-southeast1', 'asia-southeast2',
          'australia-southeast1', 'australia-southeast2',
          'southamerica-east1', 'southamerica-west1',
          'northamerica-northeast1', 'northamerica-northeast2'
        ],
        validation: validateRegion,
      },
      {
        key: 'tier',
        label: 'Machine Type',
        type: 'select',
        required: true,
        default: 'db-f1-micro',
        options: [
          'db-f1-micro', 'db-g1-small',
          'db-standard-1', 'db-standard-2', 'db-standard-4', 'db-standard-8',
          'db-n1-standard-1', 'db-n1-standard-2', 'db-n1-standard-4', 'db-n1-standard-8',
          'db-n1-highmem-2', 'db-n1-highmem-4'
        ],
        description: 'Type de machine (db-standard = nouvelle génération, moins cher que db-n1)',
      },
      {
        key: 'disk_size',
        label: 'Disk Size (GB)',
        type: 'number',
        required: false,
        default: 10,
        description: 'Taille du disque en GB',
      },
      {
        key: 'availability_type',
        label: 'Availability Type',
        type: 'select',
        required: false,
        default: 'ZONAL',
        options: ['ZONAL', 'REGIONAL'],
        description: 'Type de disponibilité',
      },
    ],
    relationshipSuggestions: {
      inbound: ['google_compute_subnetwork', 'google_compute_instance', 'google_app_engine_application'],
      outbound: ['google_sql_database'],
    },
  },

  'google_compute_forwarding_rule': {
    displayName: 'Load Balancer',
    category: 'Network',
    description: 'Règle de transfert pour load balancing',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'lb-frontend',
        suggestions: ['lb-web', 'lb-api', 'lb-internal'],
        validation: validateName,
      },
      {
        key: 'region',
        label: 'Région',
        type: 'select',
        required: false,
        default: 'europe-west1',
        options: ['us-central1', 'us-east1', 'europe-west1', 'europe-west2', 'asia-east1'],
        description: 'Région (laissez vide pour global)',
      },
      {
        key: 'load_balancing_scheme',
        label: 'Load Balancing Scheme',
        type: 'select',
        required: false,
        default: 'EXTERNAL',
        options: ['EXTERNAL', 'INTERNAL', 'INTERNAL_MANAGED'],
        description: 'Schéma de load balancing',
      },
      {
        key: 'port_range',
        label: 'Port Range',
        type: 'text',
        required: false,
        default: '80',
        placeholder: '80 ou 80-85',
        suggestions: ['80', '443', '8080', '80-85'],
        description: 'Plage de ports',
      },
      {
        key: 'target',
        label: 'Target',
        type: 'text',
        required: true,
        placeholder: 'target-pool ou backend-service',
        description: 'Cible du load balancer',
      },
    ],
    relationshipSuggestions: {
      inbound: ['google_compute_subnetwork'],
      outbound: ['google_compute_instance', 'google_compute_instance_group'],
    },
  },

  'google_container_cluster': {
    displayName: 'GKE Cluster',
    category: 'Compute',
    description: 'Cluster Kubernetes managé Google',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'gke-cluster',
        suggestions: ['prod-gke', 'dev-gke', 'k8s-cluster'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Location',
        type: 'select',
        required: true,
        default: 'europe-west1',
        options: [
          'us-central1', 'us-east1', 'us-east4', 'us-west1', 'us-west2', 'us-west3', 'us-west4',
          'europe-west1', 'europe-west2', 'europe-west3', 'europe-west4', 'europe-west6', 'europe-north1', 'europe-central2',
          'asia-east1', 'asia-east2', 'asia-northeast1', 'asia-northeast2', 'asia-northeast3', 'asia-south1', 'asia-south2', 'asia-southeast1', 'asia-southeast2',
          'australia-southeast1', 'australia-southeast2',
          'southamerica-east1', 'southamerica-west1',
          'northamerica-northeast1', 'northamerica-northeast2'
        ],
        description: 'Région ou zone du cluster',
      },
      {
        key: 'initial_node_count',
        label: 'Initial Node Count',
        type: 'number',
        required: false,
        default: 3,
        description: 'Nombre initial de nœuds par zone',
      },
      {
        key: 'node_version',
        label: 'Node Version',
        type: 'text',
        required: false,
        placeholder: '1.27',
        suggestions: ['1.28', '1.27', '1.26'],
        description: 'Version Kubernetes des nœuds',
      },
      {
        key: 'machine_type',
        label: 'Machine Type',
        type: 'select',
        required: false,
        default: 'e2-medium',
        options: ['e2-medium', 'e2-standard-2', 'e2-standard-4', 'n2-standard-2', 'n2-standard-4'],
        description: 'Type de machine pour les nœuds',
      },
      {
        key: 'network',
        label: 'Network',
        type: 'text',
        required: false,
        default: 'default',
        placeholder: 'vpc-network',
        description: 'Réseau VPC',
      },
      {
        key: 'subnetwork',
        label: 'Subnetwork',
        type: 'text',
        required: false,
        placeholder: 'subnet-gke',
        description: 'Sous-réseau',
      },
    ],
    relationshipSuggestions: {
      inbound: ['google_compute_subnetwork'],
      outbound: ['google_container_node_pool', 'google_artifact_registry_repository'],
    },
  },

  'google_secret_manager_secret': {
    displayName: 'Secret Manager',
    category: 'Security',
    description: 'Gestionnaire de secrets pour informations sensibles',
    fields: [
      {
        key: 'secret_id',
        label: 'Secret ID',
        type: 'text',
        required: true,
        placeholder: 'db-password',
        suggestions: ['db-password', 'api-key', 'service-token'],
        validation: validateName,
      },
      {
        key: 'project',
        label: 'Project ID',
        type: 'text',
        required: false,
        placeholder: 'my-project-id',
        description: 'ID du projet GCP',
      },
      {
        key: 'replication_policy',
        label: 'Replication Policy',
        type: 'select',
        required: false,
        default: 'automatic',
        options: ['automatic', 'user_managed'],
        description: 'Politique de réplication',
      },
    ],
    relationshipSuggestions: {
      inbound: ['google_compute_instance', 'google_cloud_function', 'google_container_cluster'],
    },
  },

  'google_artifact_registry_repository': {
    displayName: 'Artifact Registry',
    category: 'Containers',
    description: 'Registre pour images Docker et artefacts',
    fields: [
      {
        key: 'repository_id',
        label: 'Repository ID',
        type: 'text',
        required: true,
        placeholder: 'my-repo',
        suggestions: ['docker-repo', 'app-images', 'npm-packages'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Location',
        type: 'select',
        required: true,
        default: 'europe-west1',
        options: [
          'us-central1', 'us-east1', 'us-east4', 'us-west1', 'us-west2', 'us-west3', 'us-west4',
          'europe-west1', 'europe-west2', 'europe-west3', 'europe-west4', 'europe-west6', 'europe-north1', 'europe-central2',
          'asia-east1', 'asia-east2', 'asia-northeast1', 'asia-northeast2', 'asia-northeast3', 'asia-south1', 'asia-south2', 'asia-southeast1', 'asia-southeast2',
          'australia-southeast1', 'australia-southeast2',
          'southamerica-east1', 'southamerica-west1',
          'northamerica-northeast1', 'northamerica-northeast2'
        ],
        description: 'Localisation du repository',
      },
      {
        key: 'format',
        label: 'Format',
        type: 'select',
        required: true,
        default: 'DOCKER',
        options: ['DOCKER', 'MAVEN', 'NPM', 'PYTHON', 'APT', 'YUM'],
        description: 'Format des artefacts',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'text',
        required: false,
        placeholder: 'Repository for Docker images',
      },
    ],
    relationshipSuggestions: {
      outbound: ['google_container_cluster', 'google_cloud_run_service'],
    },
  },

  'google_compute_firewall': {
    displayName: 'Firewall Rule',
    category: 'Security',
    description: 'Règle de pare-feu pour contrôler le trafic réseau',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'allow-http',
        suggestions: ['allow-http', 'allow-https', 'allow-ssh'],
        validation: validateName,
      },
      {
        key: 'network',
        label: 'Network',
        type: 'text',
        required: true,
        placeholder: 'vpc-network',
        description: 'Nom du réseau VPC',
      },
      {
        key: 'direction',
        label: 'Direction',
        type: 'select',
        required: false,
        default: 'INGRESS',
        options: ['INGRESS', 'EGRESS'],
        description: 'Direction du trafic',
      },
      {
        key: 'priority',
        label: 'Priority',
        type: 'number',
        required: false,
        default: 1000,
        description: 'Priorité de la règle (0-65535)',
      },
      {
        key: 'source_ranges',
        label: 'Source Ranges',
        type: 'textarea',
        required: false,
        placeholder: '["0.0.0.0/0"]',
        description: 'Plages IP sources (JSON array)',
      },
      {
        key: 'allow_protocol',
        label: 'Protocol',
        type: 'select',
        required: false,
        default: 'tcp',
        options: ['tcp', 'udp', 'icmp', 'esp', 'ah', 'sctp'],
        description: 'Protocole à autoriser',
      },
      {
        key: 'allow_ports',
        label: 'Ports',
        type: 'text',
        required: false,
        placeholder: '80,443',
        suggestions: ['80', '443', '22', '80,443'],
        description: 'Ports à autoriser (séparés par des virgules)',
      },
    ],
    relationshipSuggestions: {
      inbound: ['google_compute_network'],
      outbound: ['google_compute_instance'],
    },
  },

  'google_composer_environment': {
    displayName: 'Cloud Composer',
    category: 'Data',
    description: 'Apache Airflow managé pour orchestration de workflows',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'composer-env',
        suggestions: ['airflow-prod', 'composer-dev', 'data-pipeline'],
        validation: validateName,
      },
      {
        key: 'region',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'europe-west1',
        options: [
          'us-central1', 'us-east1', 'us-east4', 'us-west1', 'us-west2', 'us-west3', 'us-west4',
          'europe-west1', 'europe-west2', 'europe-west3', 'europe-west4', 'europe-west6', 'europe-north1', 'europe-central2',
          'asia-east1', 'asia-east2', 'asia-northeast1', 'asia-northeast2', 'asia-northeast3', 'asia-south1', 'asia-south2', 'asia-southeast1', 'asia-southeast2',
          'australia-southeast1', 'australia-southeast2',
          'southamerica-east1', 'southamerica-west1',
          'northamerica-northeast1', 'northamerica-northeast2'
        ],
        validation: validateRegion,
      },
      {
        key: 'environment_size',
        label: 'Environment Size',
        type: 'select',
        required: false,
        default: 'small',
        options: ['small', 'medium', 'large'],
        description: 'Taille de l\'environnement (small: dev/test, medium: production, large: haute charge)',
      },
      {
        key: 'node_count',
        label: 'Node Count',
        type: 'number',
        required: false,
        default: 3,
        description: 'Nombre de nœuds du cluster GKE',
      },
      {
        key: 'python_version',
        label: 'Python Version',
        type: 'select',
        required: false,
        default: '3',
        options: ['2', '3'],
        description: 'Version de Python pour Airflow',
      },
      {
        key: 'airflow_version',
        label: 'Airflow Version',
        type: 'text',
        required: false,
        placeholder: '2.5.1',
        suggestions: ['2.5.1', '2.4.3', '2.3.4'],
        description: 'Version d\'Apache Airflow',
      },
    ],
    relationshipSuggestions: {
      inbound: ['google_compute_subnetwork'],
      outbound: ['google_storage_bucket', 'google_sql_database_instance', 'google_bigquery_dataset'],
    },
  },

  'google_service_account': {
    displayName: 'Service Account',
    category: 'Security',
    description: 'Compte de service pour l\'authentification et les permissions',
    fields: [
      {
        key: 'account_id',
        label: 'Account ID',
        type: 'text',
        required: true,
        placeholder: 'my-service-account',
        suggestions: ['app-service-account', 'gke-service-account', 'vm-service-account'],
        validation: validateName,
      },
      {
        key: 'display_name',
        label: 'Display Name',
        type: 'text',
        required: false,
        placeholder: 'My Service Account',
        description: 'Nom d\'affichage du compte',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'text',
        required: false,
        placeholder: 'Service account for application',
      },
      {
        key: 'project',
        label: 'Project ID',
        type: 'text',
        required: false,
        placeholder: 'my-project-id',
        description: 'ID du projet GCP',
      },
    ],
    relationshipSuggestions: {
      outbound: ['google_compute_instance', 'google_container_cluster', 'google_cloud_function'],
    },
  },
};

/**
 * Obtenir la configuration d'une ressource par son type Terraform
 */
export const getResourceConfig = (tfKey: string): ResourceConfig | null => {
  return GCP_RESOURCE_CONFIG[tfKey] || null;
};

/**
 * Obtenir les suggestions pour un champ spécifique
 */
export const getFieldSuggestions = (tfKey: string, fieldKey: string): string[] => {
  const config = getResourceConfig(tfKey);
  if (!config) return [];

  const field = config.fields.find(f => f.key === fieldKey);
  return field?.suggestions || field?.options || [];
};
